import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import logEvent, { LogEvent, LogEventStatus } from 'utils/logEvent';
import {
  setLocalWithExpiration,
  getLocalWithExpiration,
} from 'utils/handleLocalWithExpiration';

const AUTH_ERROR_CODE = 'E0000004';
const RATE_LIMIT_ERROR_CODE = 'E0000047';
const INVALID_PASSCODE_ERROR_CODE = 'E0000068';
const API_RATE_LIMIT_ERROR_CODE = '429';
export const CLOCK_SKEW_ERROR_MESSAGES: string[] = [
  'The JWT expired and is no longer valid',
  'The JWT expired before it was issued',
  'The JWT was issued in the future',
];

/**
 * Provides error message based on error and username provided
 *
 * @example
 * const [errorMessage, setErrorMessage] = useHandleAuthErrors();
 * setErrorMessage(error, username);
 * setErrorMessage(null);
 */
const useHandleAuthErrors = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState<string | null>();

  // This function is returned as the setter function.
  const getErrorMessage = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (error?: Record<string, string> | null | any, username?: string) => {
      if (!error || !username) {
        return setErrorMessage(null);
      }
      let err = 'Unable to sign in. Please try again.';
      let errorType = LogEventStatus.HandledError;

      // TODO: figure out the correct code for rate limit from one-id-mgmt
      if (
        error?.errorCode === RATE_LIMIT_ERROR_CODE ||
        error?.errorCode === API_RATE_LIMIT_ERROR_CODE
      ) {
        err =
          "We're currently experiencing limited availability. Please try again.";
        errorType = LogEventStatus.UnhandledError;
      } else if (error?.errorCode === INVALID_PASSCODE_ERROR_CODE) {
        err = "Your code doesn't match our records. Please try again.";
        errorType = LogEventStatus.HandledError;
      } else if (
        error?.errorCode !== AUTH_ERROR_CODE &&
        error?.errorCode !== 'INTERNAL'
      ) {
        err = 'An unexpected error occurred. Please try again.';
        errorType = LogEventStatus.UnhandledError;
      }

      if (location.pathname === '/login/sso/admin') {
        logEvent(LogEvent.BreakGlass, errorType, {
          username,
          error,
        });
      } else {
        logEvent(LogEvent.Login, errorType, {
          username,
          error,
        });
      }

      if (CLOCK_SKEW_ERROR_MESSAGES.includes(error.errorSummary)) {
        navigate('/clock-error');
      } else {
        const attempts = getLocalWithExpiration('attempts');
        attempts < 3 &&
          setLocalWithExpiration(
            'attempts',
            attempts + 1,
            600000 /* 10minutes */,
          );
        let storedErrorCode;
        if (error?.xhr) {
          storedErrorCode = error.xhr.status;
        } else {
          storedErrorCode = 'none';
        }
        setLocalWithExpiration('errorCode', storedErrorCode, 600000);
      }
      return setErrorMessage(err);
    },
    [location.pathname, navigate],
  );

  return [errorMessage, getErrorMessage] as const;
};

export default useHandleAuthErrors;
